import React, { useState, useEffect } from 'react';
import { Table, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import * as API from '../services/api';
import { customStyles, pageShellStyle } from '../theme';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import PageSearchInput from '../components/PageSearchInput';

const Deploy = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    const fetchProjects = async () => {
      setLoading(true);
      try {
        if (!token) {
          if (alive) setProjects([]);
          return;
        }
        const response = await API.listRbacProjects(token, null, { signal: controller.signal });
        const data = Array.isArray(response.data) ? response.data : [];
        const activeProjects = data
          .map((p) => {
            const meta = p.meta || {};
            const deployedFromMeta =
              meta.embedding_status === 'completed' && meta.vector_status === 'completed';
            return {
              ...p,
              created_at: meta.created_at || meta.updated_at || null,
              embedding_status: meta.embedding_status || 'pending',
              vector_status: meta.vector_status || 'pending',
              deleted: meta.deleted || false,
              deployed: p.chat_available != null ? Boolean(p.chat_available) : deployedFromMeta,
            };
          })
          .filter((p) => !p.deleted)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        if (alive) setProjects(activeProjects);
      } catch (error) {
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          console.error('Error fetching projects:', error);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchProjects();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [token]);

  const columns = [
    {
      title: '#',
      key: 'serial',
      width: '5%',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '30%',
    },
    {
      title: 'Created On',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (date) => {
        if (!date) return '—';
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      },
    },
    {
      title: 'Status',
      dataIndex: 'deployed',
      key: 'deployed',
      width: '15%',
      render: (deployed) => {
        const { color, fontSize, fontWeight, fontFamily, whiteSpace } = deployed
          ? customStyles.deployedBadge
          : customStyles.notDeployedBadge;
        return (
          <span style={{ color, fontSize, fontWeight, fontFamily, whiteSpace }}>
            {deployed ? 'Deployed' : 'Not Deployed'}
          </span>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const q = (searchText || '').toLowerCase().trim();
  const filteredProjects = projects.filter((p) => {
    if (!q) return true;
    return String(p.name || '').toLowerCase().includes(q)
      || String(p.description || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ ...pageShellStyle, display: 'flex', flexDirection: 'column' }}>
      <PageHeader title="RAG Pipeline Projects" />
      <PageSearchInput
        placeholder="Search projects..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onClear={() => setSearchText('')}
      />
      <Table
        columns={columns}
        dataSource={filteredProjects}
        rowKey="project_id"
        onRow={(record) => ({
          onClick: () => navigate(`/project/${record.project_id}?tab=file_pipeline`),
          style: { cursor: 'pointer' },
        })}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default Deploy;
